import type { ReactNode } from "react";
import Image from "@/components/ui/image.tsx";
import {
    TabContent,
    Tabs,
    type TabsContentProps,
    TabsList,
    TabsProvider,
    TabsTrigger,
    type TabsTriggerProps,
    useTabs,
} from "@/components/ui/tabs.tsx";
import TooltipComponent from "@/components/ui/tooltip.tsx";
import useAllParts from "@/features/sub-families/hooks/use-all-parts.ts";
import useConfigCode from "@/features/sub-families/hooks/use-config-code.ts";
import useParts from "@/features/sub-families/hooks/use-parts.ts";
import { formatColour } from "@/features/sub-families/utils/colours.ts";
import { getPartDescriptor } from "@/features/sub-families/utils/parts.ts";
import { useTranslations } from "@/features/translations/components/translations.tsx";
import { useAppSelector } from "@/redux/store.ts";
import type { ImageType } from "@/types";
import { getPlaceholderImage } from "@/utils/image.ts";
import { cn } from "@/utils/style.ts";
import Button, { type ButtonProps } from "../../../../../components/ui/button.tsx";
import Swatch from "../../../../../components/ui/swatch.tsx";

const ALL_PARTS = "all-parts";

export default function FinishingColours() {
    const t = useTranslations();
    const allParts = useAppSelector((state) => state.parts.allParts);

    const { getConfigCodeByDescriptor } = useConfigCode();

    const { hasAllParts, allPartsColours, selectedColourCode, selectAllPartsFinishingColour } = useAllParts();
    const { allowedParts, selectFinishingColour } = useParts();

    const initialTab = hasAllParts ? ALL_PARTS : allowedParts[0]?.id;

    return (
        <TabsProvider initialTab={initialTab}>
            <Tabs className="filters--finishing-colours">
                <TabsList className="tabs__list--thumbs">
                    {hasAllParts && (
                        <FinishingColourTab tab={ALL_PARTS}>
                            <Image image={allParts ?? getPlaceholderImage()} alt="" />
                            <div>{t("all_parts")}</div>
                        </FinishingColourTab>
                    )}
                    {allowedParts.map((part) => {
                        return (
                            <FinishingColourTab tab={part.id} key={part.id}>
                                <Image image={part.assetView ?? getPlaceholderImage()} alt="" />
                                <div>{part.label}</div>
                            </FinishingColourTab>
                        );
                    })}
                </TabsList>
                {hasAllParts && (
                    <FinishingColoursTabContent tab={ALL_PARTS}>
                        {allPartsColours.map((allPartsColour) => {
                            const isActive = selectedColourCode === allPartsColour.code;

                            return (
                                <FinishingColourSwatch
                                    key={allPartsColour.id}
                                    className={cn(isActive && "active")}
                                    image={allPartsColour.assetView ?? getPlaceholderImage()}
                                    onClick={() => selectAllPartsFinishingColour(allPartsColour.code)}
                                    content={formatColour(allPartsColour)}
                                />
                            );
                        })}
                    </FinishingColoursTabContent>
                )}
                {allowedParts.map((part) => {
                    const descriptor = getPartDescriptor(part);
                    const configCode = getConfigCodeByDescriptor(descriptor);

                    return (
                        <FinishingColoursTabContent tab={part.id} key={part.id}>
                            {Object.entries(part.colors)
                                .toSorted(([, a], [, b]) => a.colourId.localeCompare(b.colourId))
                                .map(([key, colour]) => {
                                    const isActive = configCode === colour.code;

                                    return (
                                        <FinishingColourSwatch
                                            key={key}
                                            className={cn(isActive && "active")}
                                            image={colour.assetView ?? getPlaceholderImage()}
                                            onClick={() => selectFinishingColour(part, descriptor, colour.code)}
                                            content={formatColour(colour)}
                                        />
                                    );
                                })}
                        </FinishingColoursTabContent>
                    );
                })}
            </Tabs>
        </TabsProvider>
    );
}

function FinishingColourTab({ children, tab, className, ...props }: TabsTriggerProps) {
    const { activeTab } = useTabs();

    const isActive = activeTab === tab;

    return (
        <TabsTrigger
            {...props}
            tab={tab}
            className={cn("filters--finishing-colours__tab-trigger", isActive && "active", className)}
        >
            {children}
        </TabsTrigger>
    );
}

function FinishingColoursTabContent({ children, className, ...props }: TabsContentProps) {
    return (
        <TabContent {...props} className="filters--finishing-colours__tab-content-wrapper">
            <div className={cn("filters--finishing-colours__tab-content", className)}>{children}</div>
        </TabContent>
    );
}

type FinishingColourSwatchProps = Omit<ButtonProps, "children"> & { image: ImageType; content: ReactNode };
function FinishingColourSwatch({ image, content, className, ...props }: FinishingColourSwatchProps) {
    return (
        <TooltipComponent
            trigger={
                <Button {...props} className={cn("filters--finishing-colours__swatch", className)}>
                    <Swatch image={image} />
                </Button>
            }
        >
            {content}
        </TooltipComponent>
    );
}
